import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, EnvelopeSimple } from "@phosphor-icons/react";
import { Brand } from "@/components/Brand";
import { forgotPasswordApi, resetPasswordApi, formatApiErrorDetail } from "@/lib/api";

const field = "h-12 w-full rounded-xl border border-black/10 bg-white px-4 text-[15px] text-ink outline-none placeholder:text-mist/70 focus:border-brand focus:ring-4 focus:ring-brand/15";
const primary = "flex h-12 w-full items-center justify-center gap-2 rounded-full bg-brand text-[15px] font-semibold text-white transition-colors hover:bg-brand-hover disabled:opacity-60";

function Shell({ title, subtitle, children }) {
  return (
    <div className="stp-bg flex min-h-screen flex-col items-center justify-center px-5 py-10">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-sm">
        <div className="mb-10 flex justify-center"><Brand /></div>
        <h1 className="text-center font-display text-4xl font-semibold text-ink">{title}</h1>
        <p className="mt-2 text-center text-base text-mist">{subtitle}</p>
        <div className="mt-8">{children}</div>
        <p className="mt-6 text-center text-sm text-mist"><Link to="/login" data-testid="back-to-login-link" className="font-semibold text-brand">Back to sign in</Link></p>
      </motion.div>
    </div>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await forgotPasswordApi(email);
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <Shell title="Check your inbox." subtitle="If an account exists for that email, we've sent a link to reset your password.">
        <div data-testid="forgot-sent" className="flex items-center gap-3 rounded-2xl bg-brand-soft p-4 text-sm text-ink">
          <EnvelopeSimple size={22} className="shrink-0 text-brand" /> The link works for 60 minutes. Check your spam folder if you don't see it.
        </div>
      </Shell>
    );
  }
  return (
    <Shell title="Forgot password?" subtitle="Enter your email and we'll send you a reset link.">
      <form onSubmit={submit} className="space-y-3">
        <input data-testid="forgot-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={field} />
        <button data-testid="forgot-submit-button" type="submit" disabled={busy} className={primary}>
          {busy ? "Sending…" : "Send reset link"} {!busy && <ArrowRight size={16} weight="bold" />}
        </button>
      </form>
    </Shell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await resetPasswordApi(params.get("token") || "", password);
      toast.success("Password updated. Please sign in.");
      navigate("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title="New password." subtitle="Choose a new password for your account.">
      <form onSubmit={submit} className="space-y-3">
        <input data-testid="reset-password-input" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password (6+ characters)" className={field} />
        <input data-testid="reset-confirm-input" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm new password" className={field} />
        <button data-testid="reset-submit-button" type="submit" disabled={busy} className={primary}>
          {busy ? "Saving…" : "Save new password"} {!busy && <ArrowRight size={16} weight="bold" />}
        </button>
      </form>
    </Shell>
  );
}
